/**
 * Central axios instance shared by every API module.
 *
 * Responsibilities:
 * - Base URL + httpOnly-cookie credentials for all requests.
 * - Normalize every rejected request to an object with a `message` string
 *   (backend errors use `{ message }`; legacy `{ error }` is also handled),
 *   while preserving any extra fields (e.g. `error_code`, `request_id`).
 * - Auto-logout on 401 for authenticated sessions.
 */
import axios from 'axios';

import API_BASE_URL from '../config';
import { isAuthenticated, clearAuthenticated } from '../storage';

const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

client.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const data = error.response?.data;

        // Auto-logout on 401 (expired/invalid token) for active sessions.
        if (status === 401 && isAuthenticated()) {
            clearAuthenticated();
            window.location.href = '/login';
        }

        const message =
            (data && (data.message || data.error)) ||
            error.message ||
            'Something went wrong';

        return Promise.reject({ ...(data && typeof data === 'object' ? data : {}), message });
    }
);

export default client;
